import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children, token }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [onlineFriends, setOnlineFriends] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Polling fallback for notifications
  const pollNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications?limit=10`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (e) {
      // Polling error - will retry on next cycle
    }
  }, [token]);

  // Poll online friends
  const pollOnlineFriends = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/online-friends`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Check for newly online friends
        data.forEach((friend) => {
          if (!onlineFriends.find((f) => f.user_id === friend.user_id)) {
            toast.success(`${friend.name} is now online!`, {
              icon: "🟢",
              duration: 3000,
            });
          }
        });
        setOnlineFriends(data);
      }
    } catch (e) {
      // Will retry on next cycle
    }
  }, [token, onlineFriends]);

  const handleFriendStatus = useCallback((data) => {
    if (data.is_online) {
      setOnlineFriends((prev) => {
        if (!prev.find(f => f.user_id === data.user_id)) {
          toast.success(`${data.name} is now online!`, { icon: "🟢", duration: 3000 });
          return [...prev, { user_id: data.user_id, name: data.name, picture: data.picture }];
        }
        return prev;
      });
    } else {
      setOnlineFriends((prev) => prev.filter(f => f.user_id !== data.user_id));
    }
  }, []);

  const showNotificationToast = useCallback((notification) => {
    const icons = { match: "💖", superlike: "⭐", message: "💬", favorite: "🌟" };
    toast(notification.title, {
      description: notification.body,
      icon: icons[notification.type] || "🔔",
      duration: 5000,
    });
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(notification.title, { body: notification.body, icon: "/favicon.ico" });
    }
  }, []);

  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case "notification":
        setNotifications((prev) => [data.notification, ...prev]);
        showNotificationToast(data.notification);
        break;
      case "new_message":
        window.dispatchEvent(new CustomEvent("new_message", { detail: data.message }));
        break;
      case "friend_status":
        handleFriendStatus(data);
        break;
      case "typing":
        setTypingUsers((prev) => ({
          ...prev,
          [data.match_id]: data.is_typing ? data.user_id : null
        }));
        break;
      case "rtc_offer":
      case "rtc_answer":
      case "rtc_ice":
      case "rtc_join":
      case "rtc_leave":
        // Forward WebRTC signaling to LiveRTC page via custom event
        window.dispatchEvent(new CustomEvent("rtc_signal", { detail: data }));
        break;
      case "pong":
        break;
      default:
        break;
    }
  }, [handleFriendStatus, showNotificationToast]);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;

    // Poll every 10 seconds
    pollingIntervalRef.current = setInterval(() => {
      pollNotifications();
      pollOnlineFriends();
    }, 10000);

    // Initial poll
    pollNotifications();
    pollOnlineFriends();
  }, [pollNotifications, pollOnlineFriends]);

  const connect = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");

    try {
      const ws = new WebSocket(`${wsUrl}/api/ws/${token}`);

      ws.onopen = () => {
        // WebSocket connected
        setIsConnected(true);

        // Stop polling when WS is connected
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (e) {
          // Skip malformed message
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        clearInterval(pingIntervalRef.current);
        startPolling();
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 5000);
      };

      ws.onerror = () => {
        setIsConnected(false);
        startPolling();
      };

      wsRef.current = ws;
    } catch (e) {
      setIsConnected(false);
      startPolling();
    }
  }, [token, handleMessage, startPolling]);

  const sendTyping = (matchId, isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "typing",
        match_id: matchId,
        is_typing: isTyping,
      }));
    }
  };

  // Send a WebRTC signaling message (offer/answer/ice/join/leave) to a target peer.
  const sendRtc = useCallback((type, targetUserId, sessionId, payload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type,
        target_user_id: targetUserId,
        session_id: sessionId,
        payload,
      }));
    }
  }, []);

  const disconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    clearTimeout(reconnectTimeoutRef.current);
    clearInterval(pingIntervalRef.current);
    clearInterval(pollingIntervalRef.current);
  };

  useEffect(() => {
    if (token) {
      connect();
      // Start polling immediately as backup
      startPolling();

      // Request notification permission
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
    }

    return () => {
      disconnect();
    };
  }, [token, connect, startPolling]);

  return (
    <WebSocketContext.Provider
      value={{
        isConnected,
        notifications,
        onlineFriends,
        typingUsers,
        sendTyping,
        sendRtc,
        setNotifications,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
